/**
 * motion.js — BecomeFounder theme motion utilities.
 *
 * Six effects, all transform/opacity/text-content only. Five are
 * IntersectionObserver-driven (never scroll-position/pixel math — see
 * GOTCHAS.md); the sixth (#4, the stepper) is deliberately NOT scroll-linked
 * at all — see its own comment for why:
 *   1. Reveal-on-scroll  — arms `.js-reveal` elements, adds `.is-visible`
 *      once ~10% is in view, then unobserves.
 *   2. Grid/section-head stagger — direct children of `[data-stagger]` get
 *      an incremental `--stagger-delay`, capped at 6 steps (~420ms tail).
 *   3. Theme-flip seam   — cross-fades `[data-flip-overlay]` elements via
 *      opacity as they cross the viewport center (the physical seam between
 *      two opposite-band sections). Never touches `background-color`.
 *   4. Click-driven stepper (Phase 7 — REPLACES the Phase 4/M3 sticky-pinned
 *      scrollytelling `initShowcase()`, removed this pass along with the
 *      scroll-track markup it drove; see sections/how-it-works.liquid).
 *      `initStepper()` wires the WAI-ARIA Tabs pattern for each
 *      `[data-stepper]` root: click/Home/End/roving-tabindex arrow-key nav
 *      on the `[data-stepper-tab]` node track, a single
 *      `transform:scaleX()` write on the gold progress line per step
 *      change, and `hidden`-attribute + scale-settle-replay swapping across
 *      the `[data-stepper-panel]` cards. No IntersectionObserver — this is
 *      purely click/keyboard-driven, never scroll-linked (the client's
 *      whole complaint about the old showcase was scroll-lag).
 *   5. Stat count-up (M5, Phase 4) — animates `[data-count-value]` text from
 *      0 to its target via `requestAnimationFrame` once it scrolls into
 *      view, then unobserves. Non-numeric characters (dashes, "+", etc.)
 *      are preserved verbatim; only digit runs animate. Reused as-is (same
 *      selector, same function) for the package price figures — Phase 6
 *      P0.1 only adds the `data-count-value` attribute in
 *      sections/packages.liquid, no fork here.
 *   6. Route-motif path draw (Phase 6, P1.1) — `initPathDraw()` arms
 *      `[data-path-draw]` wrappers the same way `initReveal()` arms
 *      `.js-reveal` (add `.is-armed` before observing, add `.is-drawn` once
 *      ~10% is in view, unobserve). CSS (stats.css) owns the actual
 *      `stroke-dashoffset` sweep on the inlined `.route-motif__draw` path
 *      (snippets/route-motif-inline.liquid) — this function only toggles
 *      the class, exactly like every other reveal in this file.
 *
 * FAIL-SOFT CONTRACT: every effect is purely additive.
 *   - `.js-reveal` is visible by default in CSS (motion.css); this script
 *     only ever ADDS the `.js-reveal--armed` class that opts an element into
 *     the hidden pre-reveal state, and only after confirming both
 *     `IntersectionObserver` support and that `prefers-reduced-motion` is
 *     not set. If this script fails to load, throws, or IO is unsupported,
 *     content stays at its default fully-visible state — never stuck at
 *     `opacity: 0`.
 *   - `[data-count-value]` elements are pre-rendered with their exact final
 *     text (Liquid) — the count-up only re-renders that same text
 *     progressively; if JS never runs, the correct final value is already
 *     on the page.
 *   - `initStepper()`'s interactive tablist/showcase/Prev-Next controls stay
 *     `display:none` (how-it-works.css) until `.is-interactive` is added to
 *     the `[data-stepper]` root as the LAST line of a successful run — if
 *     this function throws before reaching it, or never runs at all, the
 *     section stays in its server-rendered `.how-it-works__navlist` stacked
 *     list (every step fully visible, image inline, no JS dependency),
 *     never a half-wired or broken tab widget.
 *   - Every public function is wrapped so one failure can't break another;
 *     `init()` itself is wrapped so a thrown error never blocks the rest of
 *     the page (content/CTAs must work with JS disabled or broken).
 *   - `[data-path-draw]`'s inner path is CSS-default `stroke-dashoffset: 0`
 *     (fully drawn) — mirroring `.js-reveal`'s "visible unless armed"
 *     contract exactly. `initPathDraw()` only ever ADDS `.is-armed`, so a
 *     script failure/IO-unsupported browser leaves the line fully drawn,
 *     never a blank gap.
 *
 * Loaded as `<script type="module" defer>` — no globals, no dependencies.
 */

const LOG_PREFIX = "[BecomeFounder]";
const REVEAL_THRESHOLD = 0.1;
const STAGGER_STEP_MS = 70;
const STAGGER_STEP_CAP = 6;
const COUNT_DURATION_MS = 1100;
// Matches initCounters()'s own threshold (not REVEAL_THRESHOLD) so the
// stats route-motif draw and the stat count-up fire on the same frame and
// finish together (P1.1 — "pair the draw to arrive with the count-up").
// Keep COUNT_DURATION_MS (JS) and stats.css's stroke-dashoffset transition
// duration in sync if either ever changes.
const PATH_DRAW_THRESHOLD = 0.4;

function prefersReducedMotion() {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function supportsIntersectionObserver() {
  return "IntersectionObserver" in window;
}

/**
 * Reveal-on-scroll. Only "arms" elements (i.e. opts them into the hidden
 * pre-reveal state) once we know we can also reveal them again.
 *
 * `will-change` is set only for the brief window an element is actually
 * transitioning (from the moment it enters the viewport to the end of its
 * opacity/transform transition), then cleared — not left on every armed
 * element for the whole page lifetime (Phase 3→4 code-review carry-forward).
 */
function initReveal(reduced) {
  const els = document.querySelectorAll(".js-reveal");
  if (!els.length || reduced || !supportsIntersectionObserver()) {
    return;
  }

  let observer;
  try {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target;
            target.style.willChange = "opacity, transform";
            target.classList.add("is-visible");
            target.addEventListener(
              "transitionend",
              () => {
                target.style.willChange = "auto";
              },
              { once: true }
            );
            observer.unobserve(target);
          }
        });
      },
      { threshold: REVEAL_THRESHOLD }
    );
  } catch (err) {
    console.warn(`${LOG_PREFIX} reveal observer failed to initialize`, err);
    return;
  }

  els.forEach((el) => {
    el.classList.add("js-reveal--armed");
    observer.observe(el);
  });
}

/**
 * Grid stagger: sets `--stagger-delay` on direct children of every
 * `[data-stagger]` container. Pure CSS custom-property assignment — safe to
 * run even under reduced motion (motion.css zeroes it out there anyway).
 */
function initStagger() {
  document.querySelectorAll("[data-stagger]").forEach((parent) => {
    Array.from(parent.children).forEach((child, index) => {
      const step = Math.min(index, STAGGER_STEP_CAP);
      child.style.setProperty("--stagger-delay", `${step * STAGGER_STEP_MS}ms`);
    });
  });
}

/**
 * Theme-flip seam cross-fade. The band colors themselves are already
 * correct from Liquid-rendered `data-theme` — this only adds the cosmetic
 * opacity cue as the seam between two bands scrolls through view, so it is
 * safe to skip entirely under reduced motion or missing IO support.
 */
function initThemeFlip(reduced) {
  const overlays = document.querySelectorAll("[data-flip-overlay]");
  if (!overlays.length || reduced || !supportsIntersectionObserver()) {
    return;
  }

  let observer;
  try {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle("is-active", entry.isIntersecting);
        });
      },
      // Narrow the effective viewport to a band around its vertical center,
      // so the cross-fade reads as "crossing the seam" rather than firing
      // the instant the overlay is merely on-screen.
      { threshold: 0, rootMargin: "-45% 0px -45% 0px" }
    );
  } catch (err) {
    console.warn(`${LOG_PREFIX} theme-flip observer failed to initialize`, err);
    return;
  }

  overlays.forEach((el) => observer.observe(el));
}

/**
 * Phase 7 — click-driven horizontal stepper (WAI-ARIA Tabs pattern). See the
 * file header comment (effect #4) for the architecture summary. Runs
 * UNCONDITIONALLY (unlike the purely-decorative effects above) — this is
 * the actual navigation mechanism for the interactive stepper UI, not an
 * optional enhancement layered on always-present content, so it must not be
 * skipped under reduced motion. `reduced` only changes HOW a step change is
 * animated (instant vs. the scale-settle replay), never WHETHER the stepper
 * works.
 */
function initStepper(reduced) {
  const roots = document.querySelectorAll("[data-stepper]");
  if (!roots.length) return;

  roots.forEach((root) => {
    try {
      const tabs = Array.from(root.querySelectorAll("[data-stepper-tab]"));
      const panels = Array.from(root.querySelectorAll("[data-stepper-panel]"));
      const progress = root.querySelector("[data-stepper-progress]");
      const tablist = root.querySelector("[data-stepper-tablist]");
      const prevBtn = root.querySelector("[data-stepper-prev]");
      const nextBtn = root.querySelector("[data-stepper-next]");
      if (!tabs.length || !panels.length) return;

      const total = tabs.length;
      let activeIndex = Math.max(
        tabs.findIndex((tab) => tab.classList.contains("is-active")),
        0
      );

      function updateProgress(index) {
        if (!progress) return;
        // Step 1 (index 0) = 0%, the last step = 100% — a stepper reads as
        // "how far through," unlike the old vertical scroll-rail's
        // (activeIndex + 1) / total (see how-it-works.css header comment).
        const ratio = index / Math.max(total - 1, 1);
        progress.style.transform = `scaleX(${ratio})`;
      }

      function updateControls(index) {
        // `aria-disabled` (not the native `disabled` attribute) so a
        // repeated click right at the boundary never yanks focus off the
        // button — it just becomes a no-op while remaining reachable.
        if (prevBtn) prevBtn.setAttribute("aria-disabled", String(index === 0));
        if (nextBtn) nextBtn.setAttribute("aria-disabled", String(index === total - 1));
      }

      function setActiveStep(index) {
        index = Math.max(0, Math.min(total - 1, index));
        if (index === activeIndex) return;
        activeIndex = index;

        tabs.forEach((tab, i) => {
          const isActive = i === index;
          tab.classList.toggle("is-active", isActive);
          tab.classList.toggle("is-complete", i < index);
          tab.setAttribute("aria-selected", String(isActive));
          tab.tabIndex = isActive ? 0 : -1;
        });

        panels.forEach((panel, i) => {
          if (i !== index) {
            panel.hidden = true;
            panel.classList.remove("is-active");
            return;
          }
          panel.hidden = false;
          panel.classList.remove("is-active");
          if (reduced) {
            panel.classList.add("is-active");
          } else {
            // Force a style flush so the browser registers the resting
            // (opacity:0/scale(1.04)) state before `.is-active` goes back
            // on — un-hiding and re-adding the class in the same tick can
            // otherwise collapse to a single paint with no visible
            // transition, since a `hidden` element has no prior rendered
            // box for the browser to animate from.
            void panel.offsetWidth;
            window.requestAnimationFrame(() => {
              panel.classList.add("is-active");
            });
          }
        });

        updateProgress(index);
        updateControls(index);
      }

      tabs.forEach((tab, i) => {
        tab.addEventListener("click", () => setActiveStep(i));
      });

      if (tablist) {
        tablist.addEventListener("keydown", (event) => {
          let targetIndex = null;
          if (event.key === "ArrowRight") targetIndex = (activeIndex + 1) % total;
          else if (event.key === "ArrowLeft") targetIndex = (activeIndex - 1 + total) % total;
          else if (event.key === "Home") targetIndex = 0;
          else if (event.key === "End") targetIndex = total - 1;
          if (targetIndex === null) return;
          event.preventDefault();
          setActiveStep(targetIndex);
          tabs[targetIndex].focus();
        });
      }

      // Prev/Next are supplementary buttons, not tabs — they DISABLE at the
      // ends (never wrap) and never steal focus back from the button itself
      // (unlike arrow-key nav, which follows APG and moves focus onto the
      // newly active tab).
      if (prevBtn) {
        prevBtn.addEventListener("click", () => {
          if (prevBtn.getAttribute("aria-disabled") === "true") return;
          setActiveStep(activeIndex - 1);
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener("click", () => {
          if (nextBtn.getAttribute("aria-disabled") === "true") return;
          setActiveStep(activeIndex + 1);
        });
      }

      // Sync Prev/Next disabled state + progress fill to the server-
      // rendered initial active step before revealing the interactive UI.
      updateControls(activeIndex);
      updateProgress(activeIndex);

      // LAST line of a successful run — how-it-works.css gates the entire
      // tablist/showcase/controls block behind this class (+ a 768px
      // breakpoint), so nothing becomes clickable-looking until every
      // handler above is actually wired.
      root.classList.add("is-interactive");
    } catch (err) {
      console.warn(`${LOG_PREFIX} stepper failed to initialize`, err);
    }
  });
}

/**
 * M5 — stat count-up. Parses the digit runs out of the element's
 * pre-rendered text (e.g. "7–10" → [7, 10], "135+" → [135]) and animates
 * each from 0 to its target over `COUNT_DURATION_MS`, reconstructing the
 * original string every frame so separators/suffixes ("–", "+") are
 * preserved verbatim. `font-variant-numeric: tabular-nums` (already applied
 * via the `.tabular-nums` class in every caller) means the glyph width never
 * changes mid-count — zero CLS. Elements with no digits are left untouched.
 */
function initCounters(reduced) {
  const els = document.querySelectorAll("[data-count-value]");
  if (!els.length || reduced || !supportsIntersectionObserver()) {
    return;
  }

  let observer;
  try {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateCount(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );
  } catch (err) {
    console.warn(`${LOG_PREFIX} counter observer failed to initialize`, err);
    return;
  }

  els.forEach((el) => observer.observe(el));
}

function animateCount(el) {
  const template = el.getAttribute("data-count-value") || el.textContent || "";
  const targets = (template.match(/\d+/g) || []).map(Number);
  if (!targets.length) return; // nothing numeric to animate — leave as-is

  // Animation token (code-review carry-forward, Phase 6): the package price
  // figures this function now also animates (P0.1) can ALSO be re-tweened
  // by packages.js's WY/DE roll if a shopper toggles state while this
  // scroll-triggered count-up is still mid-flight. Both writers stamp/check
  // `el.__bfAnimToken` each frame so a newer animation silently wins instead
  // of the two rAF loops fighting over `textContent` every frame.
  const token = (el.__bfAnimToken = (el.__bfAnimToken || 0) + 1);
  const start = performance.now();

  function frame(now) {
    if (el.__bfAnimToken !== token) return; // superseded — stop writing
    const elapsed = now - start;
    const progress = Math.min(elapsed / COUNT_DURATION_MS, 1);
    // Ease-out cubic — matches the restrained, no-overshoot motion language.
    const eased = 1 - Math.pow(1 - progress, 3);

    let i = 0;
    const rendered = template.replace(/\d+/g, (match) => {
      const target = targets[i];
      i += 1;
      const current = Math.min(target, Math.round(target * eased));
      return String(current).padStart(match.length, "0");
    });
    el.textContent = rendered;

    if (progress < 1) {
      window.requestAnimationFrame(frame);
    } else {
      el.textContent = template; // guarantee the exact final string
    }
  }

  window.requestAnimationFrame(frame);
}

/**
 * P1.1 — stats route-motif "draws itself". Same two-step arm/reveal
 * contract as `initReveal()`: `[data-path-draw]` wrappers start with NO
 * class, and CSS (stats.css) defaults `.route-motif__draw`'s
 * `stroke-dashoffset` to 0 (fully drawn) at that state — so if this
 * function never runs, the line is already complete. Only once we know
 * IntersectionObserver is supported and reduced-motion isn't set do we add
 * `.is-armed` (which is what CSS actually uses to retract the line pending
 * `.is-drawn`), then `.is-drawn` fires the sweep-in on ~40% visibility —
 * matched to `initCounters()`'s own threshold so the line and the stat
 * number arrive together.
 */
function initPathDraw(reduced) {
  const els = document.querySelectorAll("[data-path-draw]");
  if (!els.length || reduced || !supportsIntersectionObserver()) {
    return;
  }

  let observer;
  try {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-drawn");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: PATH_DRAW_THRESHOLD }
    );
  } catch (err) {
    console.warn(`${LOG_PREFIX} path-draw observer failed to initialize`, err);
    return;
  }

  els.forEach((el) => {
    el.classList.add("is-armed");
    observer.observe(el);
  });
}

function init() {
  try {
    const reduced = prefersReducedMotion();
    initReveal(reduced);
    initStagger();
    initThemeFlip(reduced);
    initStepper(reduced);
    initCounters(reduced);
    initPathDraw(reduced);
  } catch (err) {
    // Fail-soft: a motion bug must never break content or a CTA.
    console.warn(`${LOG_PREFIX} motion init failed — continuing without enhancement`, err);
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
